// A line-up: children or animals in a row with a "front" arrow at one end.
// Plain React SVG, like practice-turn.tsx. The only words are "front" and
// the name or word under each one; no place number is ever written.

import {
  describeLineUp,
  lineUpPicture,
  type LineUpAnimal,
  type LineUpSpec,
} from './practice-line-up-data';

const INK = '#2d4a3e';
// Shirts and skin take turns, so children next to each other look different.
const SHIRTS = [
  '#e0605a',
  '#3a78c9',
  '#f2c94c',
  '#3f9b4f',
  '#8a5bb5',
  '#ef8a2c',
];
const SKIN = ['#f2c9a0', '#c68c5c', '#e8b48a', '#8d5a3b'];
const HAIR = ['#5a3a22', '#2b2b2b', '#c9893c', '#2b2b2b'];

const line = {
  stroke: INK,
  strokeWidth: 1.5,
  strokeLinejoin: 'round' as const,
  strokeLinecap: 'round' as const,
};

// A child seen from the front, in a square of side 46 centred on (0, 0).
// Their feet point to the front of the line (`toward` is 1 for right).
function Child({ index, toward }: { index: number; toward: 1 | -1 }) {
  const shirt = SHIRTS[index % SHIRTS.length];
  const skin = SKIN[index % SKIN.length];
  const hair = HAIR[index % HAIR.length];
  return (
    <g data-figure="child">
      <line x1={-4} y1={10} x2={-4} y2={19} {...line} strokeWidth={3} />
      <line x1={4} y1={10} x2={4} y2={19} {...line} strokeWidth={3} />
      <ellipse cx={-4 + toward * 2} cy={20} rx={3.5} ry={1.8} fill={INK} />
      <ellipse cx={4 + toward * 2} cy={20} rx={3.5} ry={1.8} fill={INK} />
      <line x1={-9} y1={-3} x2={-13} y2={8} {...line} strokeWidth={3} />
      <line x1={9} y1={-3} x2={13} y2={8} {...line} strokeWidth={3} />
      <rect
        x={-9}
        y={-5}
        width={18}
        height={17}
        rx={4}
        fill={shirt}
        {...line}
      />
      <circle cx={0} cy={-13} r={8} fill={skin} {...line} />
      <path
        d="M -8 -14 A 8 8 0 0 1 8 -14 Q 4 -18 0 -17 Q -4 -18 -8 -14 Z"
        fill={hair}
      />
      <circle cx={-3} cy={-12} r={1} fill={INK} />
      <circle cx={3} cy={-12} r={1} fill={INK} />
      <path d="M -2.5 -8.5 Q 0 -6.5 2.5 -8.5" fill="none" {...line} />
    </g>
  );
}

// One drawing of each animal side on, facing right, in a square of side 46
// centred on (0, 0). The view turns it round to face a front on the left.
function Animal({ animal }: { animal: LineUpAnimal }) {
  const leg = (x: number, top: number, colour = INK) => (
    <line
      x1={x}
      y1={top}
      x2={x}
      y2={20}
      {...line}
      stroke={colour}
      strokeWidth={2.5}
    />
  );
  switch (animal) {
    case 'dog':
      return (
        <g data-figure="dog">
          <path
            d="M -14 2 Q -20 -4 -19 -10"
            fill="none"
            {...line}
            strokeWidth={2.5}
          />
          {leg(-10, 8)}
          {leg(-5, 9)}
          {leg(4, 9)}
          {leg(9, 8)}
          <ellipse cx={-2} cy={4} rx={13} ry={7} fill="#b5835a" {...line} />
          <circle cx={12} cy={-6} r={7} fill="#b5835a" {...line} />
          <ellipse cx={18} cy={-4} rx={4} ry={3} fill="#d9b48f" {...line} />
          <circle cx={21} cy={-5} r={1.6} fill={INK} />
          <ellipse cx={8.5} cy={-4} rx={3} ry={5.5} fill="#7a5233" {...line} />
          <circle cx={13} cy={-8} r={1.1} fill={INK} />
        </g>
      );
    case 'cat':
      return (
        <g data-figure="cat">
          <path
            d="M -14 5 Q -22 -2 -17 -13"
            fill="none"
            {...line}
            stroke="#7d7d7d"
            strokeWidth={3}
          />
          {leg(-10, 9)}
          {leg(-5, 10)}
          {leg(4, 10)}
          {leg(9, 9)}
          <ellipse cx={-3} cy={6} rx={12} ry={6} fill="#a3a3a3" {...line} />
          <polygon points="6,-7 7,-16 12,-10" fill="#a3a3a3" {...line} />
          <polygon points="12,-10 17,-16 17,-6" fill="#a3a3a3" {...line} />
          <circle cx={12} cy={-4} r={6.5} fill="#a3a3a3" {...line} />
          <circle cx={14} cy={-5} r={1.1} fill={INK} />
          <path
            d="M 16 -2 L 22 -3 M 16 -1 L 22 0"
            fill="none"
            {...line}
            strokeWidth={0.8}
          />
        </g>
      );
    case 'duck':
      return (
        <g data-figure="duck">
          <line
            x1={-3}
            y1={15}
            x2={-3}
            y2={20}
            {...line}
            stroke="#ef8a2c"
            strokeWidth={2.5}
          />
          <line
            x1={3}
            y1={15}
            x2={3}
            y2={20}
            {...line}
            stroke="#ef8a2c"
            strokeWidth={2.5}
          />
          <path d="M -15 4 L -21 -2 L -13 0 Z" fill="#f2cf3c" {...line} />
          <ellipse cx={-2} cy={7} rx={13} ry={8} fill="#f2cf3c" {...line} />
          <path d="M -8 5 Q -2 12 5 5" fill="none" {...line} />
          <circle cx={9} cy={-8} r={6.5} fill="#f2cf3c" {...line} />
          <polygon points="14,-10 22,-7 14,-5" fill="#ef8a2c" {...line} />
          <circle cx={10} cy={-10} r={1.1} fill={INK} />
        </g>
      );
    case 'pig':
      return (
        <g data-figure="pig">
          <path
            d="M -15 0 q -4 -2 -3 -5 q 1 -3 -3 -3"
            fill="none"
            {...line}
            stroke="#d97a92"
            strokeWidth={2}
          />
          {leg(-10, 10, '#d97a92')}
          {leg(-4, 11, '#d97a92')}
          {leg(4, 11, '#d97a92')}
          {leg(10, 10, '#d97a92')}
          <ellipse cx={-2} cy={3} rx={14} ry={10} fill="#f4a7b9" {...line} />
          <circle cx={12} cy={-3} r={7.5} fill="#f4a7b9" {...line} />
          <polygon points="8,-9 9,-16 13,-10" fill="#f4a7b9" {...line} />
          <ellipse cx={19.5} cy={-1} rx={3} ry={3.6} fill="#f7c4d0" {...line} />
          <circle cx={19} cy={-2} r={0.7} fill={INK} />
          <circle cx={19} cy={0.5} r={0.7} fill={INK} />
          <circle cx={13} cy={-5} r={1.1} fill={INK} />
        </g>
      );
    case 'goat':
      return (
        <g data-figure="goat">
          <path d="M -15 -2 L -19 -7" fill="none" {...line} strokeWidth={2.5} />
          {leg(-10, 6)}
          {leg(-5, 7)}
          {leg(4, 7)}
          {leg(9, 6)}
          <ellipse cx={-2} cy={1} rx={13} ry={7} fill="#ece8de" {...line} />
          <path
            d="M 10 -12 Q 6 -20 1 -18"
            fill="none"
            {...line}
            strokeWidth={2.5}
            stroke="#8a7a5c"
          />
          <ellipse cx={13} cy={-8} rx={5} ry={6.5} fill="#ece8de" {...line} />
          <polygon points="13,-2 17,-2 15,5" fill="#8a7a5c" {...line} />
          <ellipse cx={8} cy={-10} rx={3.5} ry={1.5} fill="#ece8de" {...line} />
          <circle cx={14} cy={-10} r={1.1} fill={INK} />
        </g>
      );
    case 'sheep':
      return (
        <g data-figure="sheep">
          {leg(-9, 8)}
          {leg(-4, 9)}
          {leg(4, 9)}
          {leg(9, 8)}
          {[
            [-12, 2],
            [-6, -4],
            [1, -5],
            [7, 0],
            [-8, 7],
            [0, 7],
            [6, 6],
            [-1, 1],
          ].map(([x = 0, y = 0], index) => (
            <circle
              key={index}
              cx={x}
              cy={y}
              r={6.5}
              fill="#f7f3e8"
              {...line}
            />
          ))}
          <ellipse cx={14} cy={-5} rx={5} ry={6.5} fill="#4a4a4a" {...line} />
          <ellipse
            cx={9.5}
            cy={-7}
            rx={3.5}
            ry={1.6}
            fill="#4a4a4a"
            {...line}
          />
          <circle cx={16} cy={-7} r={1.1} fill="#ffffff" />
        </g>
      );
    case 'hen':
      return (
        <g data-figure="hen">
          <line
            x1={-3}
            y1={12}
            x2={-3}
            y2={20}
            {...line}
            stroke="#ef8a2c"
            strokeWidth={2.2}
          />
          <line
            x1={3}
            y1={12}
            x2={3}
            y2={20}
            {...line}
            stroke="#ef8a2c"
            strokeWidth={2.2}
          />
          <path
            d="M -12 2 L -19 -9 L -15 -11 L -14 -3 Z"
            fill="#ffffff"
            {...line}
          />
          <ellipse cx={-2} cy={3} rx={12} ry={10} fill="#ffffff" {...line} />
          <path d="M -9 1 Q -2 9 5 2" fill="none" {...line} />
          <path
            d="M 5 -14 q 1 -4 3 -1 q 1 -4 3 -1 q 2 -3 3 1 Z"
            fill="#d64541"
            {...line}
          />
          <circle cx={9} cy={-9} r={5.5} fill="#ffffff" {...line} />
          <polygon points="14,-10 19,-8 14,-6" fill="#f2c94c" {...line} />
          <path
            d="M 13 -6 q 1 4 -1 5"
            fill="#d64541"
            {...line}
            strokeWidth={1}
          />
          <circle cx={10} cy={-10} r={1.1} fill={INK} />
        </g>
      );
    case 'rabbit':
      return (
        <g data-figure="rabbit">
          <circle cx={-14} cy={8} r={3.5} fill="#ffffff" {...line} />
          <ellipse cx={-3} cy={9} rx={11} ry={9} fill="#c9b8a6" {...line} />
          <ellipse cx={4} cy={18} rx={5} ry={2.2} fill="#c9b8a6" {...line} />
          <ellipse
            cx={6}
            cy={-15}
            rx={2.6}
            ry={8}
            fill="#c9b8a6"
            {...line}
            transform="rotate(-12 6 -15)"
          />
          <ellipse
            cx={11}
            cy={-15}
            rx={2.6}
            ry={8}
            fill="#c9b8a6"
            {...line}
            transform="rotate(10 11 -15)"
          />
          <circle cx={9} cy={-3} r={6.5} fill="#c9b8a6" {...line} />
          <circle cx={11} cy={-4} r={1.1} fill={INK} />
          <circle cx={15.3} cy={-2} r={1} fill="#d97a92" />
        </g>
      );
  }
}

export function LineUpView({ lineUp }: { lineUp: LineUpSpec }) {
  const picture = lineUpPicture(lineUp);
  const { arrow } = picture;
  const toward = lineUp.front === 'right' ? 1 : -1;
  const point = arrow.to > arrow.from ? -1 : 1;
  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeLineUp(lineUp)}
        viewBox={`0 0 ${String(picture.width)} ${String(picture.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(picture.width * 1.4))}px` }}
      >
        <line
          x1={picture.members[0] ? picture.members[0].x - picture.size / 2 : 0}
          y1={picture.figureY + 21}
          x2={
            (picture.members[picture.members.length - 1]?.x ?? 0) +
            picture.size / 2
          }
          y2={picture.figureY + 21}
          stroke="#c8d3c6"
          strokeWidth="2"
        />
        <g data-arrow="front">
          <line
            x1={arrow.from}
            y1={arrow.y}
            x2={arrow.to + point * 8}
            y2={arrow.y}
            stroke={INK}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <polygon
            points={`${String(arrow.to)},${String(arrow.y)} ${String(arrow.to + point * 10)},${String(arrow.y - 7)} ${String(arrow.to + point * 10)},${String(arrow.y + 7)}`}
            fill={INK}
          />
          <text
            x={(arrow.from + arrow.to) / 2}
            y={arrow.wordY}
            textAnchor="middle"
            fill={INK}
            fontSize="12"
            fontWeight="700"
          >
            front
          </text>
        </g>
        {picture.members.map((member, index) => (
          <g key={member.name} data-member={member.name}>
            <g
              transform={`translate(${String(member.x)} ${String(picture.figureY)})${
                lineUp.kind === 'animals' && lineUp.front === 'left'
                  ? ' scale(-1 1)'
                  : ''
              }`}
            >
              {lineUp.kind === 'children' ? (
                <Child index={index} toward={toward} />
              ) : (
                <Animal animal={member.name as LineUpAnimal} />
              )}
            </g>
            <text
              x={member.x}
              y={picture.nameY}
              textAnchor="middle"
              fill={INK}
              fontSize={member.name.length > 6 ? 10 : 12}
              fontWeight="600"
            >
              {member.name}
            </text>
          </g>
        ))}
      </svg>
    </figure>
  );
}
